import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LOAN_MAX_AMOUNT, LOAN_MAX_RATE, LOAN_MIN_AMOUNT, LOAN_MIN_RATE, formatDate } from '@pk/shared';
import type { Id, LoansView as LoansData } from '@pk/shared';
import { useCommand, useLoans } from '../../api/hooks';
import { Btn, ErrorBox, Inline, Sheet, Tag } from '../../components/ui';
import { ResourceIcon } from '../../components/icons';
import { Meta, NumField, SheetLayout, Skeleton } from './shared';

type Loan = LoansData['loans'][number];

export function LoansView({ actor, gold }: { actor: Id; gold: number }) {
  const { t } = useTranslation();
  const loans = useLoans();
  const [lend, setLend] = useState(false);
  const [repay, setRepay] = useState<Loan | null>(null);
  if (loans.isLoading) return <Skeleton rows={3} />;
  if (loans.isError || !loans.data) return <ErrorBox error={loans.error} onRetry={() => loans.refetch()} />;
  const rows = loans.data.loans;
  const owe = rows.filter((l) => l.role === 'borrower');
  const owed = rows.filter((l) => l.role === 'lender');
  const sum = (a: Loan[]) => Math.round(a.reduce((n, l) => n + l.outstanding, 0));

  const card = (l: Loan) => {
    const mine = l.role === 'borrower';
    const other = mine ? l.lenderName : l.borrowerName;
    return (
      <article key={l.id} className={`card tr-loan ${l.defaulted ? 'flag-red' : l.missed > 0 ? 'flag-gold' : ''}`}>
        <div className="tr-loan-top">
          <div className="tr-row-main">
            <span className="tr-row-title">{other}</span>
            <span className="tr-row-meta"><Meta items={[t('eco.loans.rate', { pct: Math.round(l.annualRate * 100) }), t('eco.loans.since', { date: formatDate(l.issuedDay) })]} /></span>
          </div>
          <div className="tr-loan-amt">
            <strong className="num">{Math.round(l.outstanding)}g</strong>
            <span className="tr-faint">{t('eco.loans.of', { n: l.principal })}</span>
          </div>
        </div>
        {(l.missed > 0 || l.defaulted) && (
          <div className="tr-tags">
            {l.missed > 0 && <Tag tone="gold">{t('trade.loans.missed', { count: l.missed })}</Tag>}
            {l.defaulted && <Tag tone="red">{t('trade.loans.defaulted')}</Tag>}
          </div>
        )}
        {mine && <Btn small variant={l.missed > 0 || l.defaulted ? 'primary' : 'ghost'} onClick={() => setRepay(l)}>{t('trade.loans.repayBtn')}</Btn>}
      </article>
    );
  };

  return (
    <section className="tr-section">
      <div className="tr-section-head">
        <p className="tr-help">{t('eco.loans.help')}</p>
        <Btn variant="primary" block onClick={() => setLend(true)}>{t('trade.loans.lend')}</Btn>
      </div>

      {rows.length === 0 ? (
        <div className="tr-empty">
          <ResourceIcon k="gold" size={40} />
          <strong>{t('trade.loans.none')}</strong>
          <span>{t('eco.loans.emptyHelp')}</span>
        </div>
      ) : (
        <>
          {owe.length > 0 && (
            <>
              <h4 className="tr-h">{t('eco.loans.owe')}<span className="tr-total neg">{sum(owe)}g</span></h4>
              <div className="tr-list">{owe.map(card)}</div>
            </>
          )}
          {owed.length > 0 && (
            <>
              <h4 className="tr-h">{t('eco.loans.owed')}<span className="tr-total pos">{sum(owed)}g</span></h4>
              <div className="tr-list">{owed.map(card)}</div>
            </>
          )}
        </>
      )}

      {lend && <LendSheet actor={actor} onClose={() => setLend(false)} />}
      {repay && <RepaySheet actor={actor} gold={gold} loan={repay} onClose={() => setRepay(null)} />}
    </section>
  );
}

function LendSheet({ actor, onClose }: { actor: Id; onClose: () => void }) {
  const { t } = useTranslation();
  const cmd = useCommand();
  const minPct = Math.round(LOAN_MIN_RATE * 100);
  const maxPct = Math.round(LOAN_MAX_RATE * 100);
  const [amount, setAmount] = useState(50);
  const [ratePct, setRatePct] = useState(10);
  const rate = ratePct / 100;
  const valid = amount >= LOAN_MIN_AMOUNT && amount <= LOAN_MAX_AMOUNT && rate >= LOAN_MIN_RATE && rate <= LOAN_MAX_RATE;
  const submit = async () => {
    const res = await cmd.run({ type: 'loan_to_liege', actorId: actor, amount, rate });
    if (res.ok) onClose();
  };
  return (
    <Sheet open onClose={onClose} fixed title={t('trade.loans.lend')}>
      <SheetLayout foot={
        <>
          <div className="tr-sum">
            <span className="tr-sum-main">{t('eco.loans.lendSummary', { amount })}</span>
            <span className="tr-sum-sub">{t('eco.loans.interest', { n: Math.round(amount * rate * 10) / 10 })}</span>
          </div>
          <Inline error={cmd.error} />
          <Btn variant="primary" block busy={cmd.pending} disabled={!valid} onClick={() => { void submit(); }}>{t('trade.loans.lendBtn')}</Btn>
        </>
      }>
        <p className="tr-help">{t('eco.loans.lendHelp')}</p>
        <NumField label={t('trade.loans.amount')} value={amount} onChange={setAmount} min={LOAN_MIN_AMOUNT} max={LOAN_MAX_AMOUNT} step={10} unit="g"
          chips={[50, 100, 250, LOAN_MAX_AMOUNT].map((v) => ({ label: v === LOAN_MAX_AMOUNT ? t('eco.order.max') : String(v), value: v }))}
          hint={t('trade.loans.amountHint', { min: LOAN_MIN_AMOUNT, max: LOAN_MAX_AMOUNT })} />
        <NumField label={t('trade.loans.rate')} value={ratePct} onChange={setRatePct} min={minPct} max={maxPct} step={5} unit="%"
          chips={[5, 10, 20, 30].map((v) => ({ label: `${v}%`, value: v }))}
          hint={t('trade.loans.rateHint', { min: minPct, max: maxPct })} />
      </SheetLayout>
    </Sheet>
  );
}

function RepaySheet({ actor, gold, loan, onClose }: { actor: Id; gold: number; loan: Loan; onClose: () => void }) {
  const { t } = useTranslation();
  const cmd = useCommand();
  const max = Math.floor(loan.outstanding);
  const [amount, setAmount] = useState(Math.max(1, max));
  const valid = amount >= 1 && amount <= loan.outstanding;
  const submit = async () => {
    const res = await cmd.run({ type: 'repay_loan', actorId: actor, loanId: loan.id, amount });
    if (res.ok) onClose();
  };
  const part = (f: number) => Math.max(1, Math.floor(max * f));
  return (
    <Sheet open onClose={onClose} fixed title={t('eco.loans.repayTitle', { name: loan.lenderName })}>
      <SheetLayout foot={
        <>
          <div className="tr-sum">
            <span className="tr-sum-main">{t('eco.loans.leftAfter', { n: Math.max(0, Math.round(loan.outstanding - amount)) })}</span>
            <span className={`tr-sum-sub ${amount > gold ? 'warn' : ''}`}>{amount > gold ? t('eco.order.tooPoor', { gold }) : t('eco.order.youHave', { gold })}</span>
          </div>
          <Inline error={cmd.error} />
          <Btn variant="primary" block busy={cmd.pending} disabled={!valid} onClick={() => { void submit(); }}>{t('trade.loans.repayBtn')}</Btn>
        </>
      }>
        <p className="tr-help">{t('eco.loans.stillOwed', { n: Math.round(loan.outstanding) })}</p>
        <NumField label={t('trade.loans.amount')} value={amount} onChange={setAmount} min={1} max={Math.max(1, max)} step={5} unit="g"
          chips={[{ label: '25%', value: part(0.25) }, { label: '50%', value: part(0.5) }, { label: t('eco.order.max'), value: max }]}
          hint={t('trade.loans.repayMax', { max })} />
      </SheetLayout>
    </Sheet>
  );
}
